import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { IconCheck, IconLoader2 } from "@tabler/icons-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils/cn";
import { getPlanCatalog, changePlan, type AgencyPlan, type AgencyPlanOption } from "../api";

function formatPrice(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function PlanCatalogDialog({
  open,
  onOpenChange,
  currentCode,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentCode: string;
}) {
  const queryClient = useQueryClient();
  const [pendingCode, setPendingCode] = useState<string | null>(null);

  const { data: catalog, isLoading } = useQuery({
    queryKey: ["agency-plan-catalog"],
    queryFn: getPlanCatalog,
    enabled: open,
  });

  const changeMutation = useMutation({
    mutationFn: changePlan,
    onSuccess: (updated) => {
      queryClient.setQueryData<AgencyPlan>(["agency-plan"], updated);
      toast.success(`Plano alterado para ${updated.name}.`);
      onOpenChange(false);
    },
    onError: () => {
      toast.error("Não foi possível alterar o plano. Tente novamente.");
    },
    onSettled: () => {
      setPendingCode(null);
    },
  });

  function selectPlan(option: AgencyPlanOption) {
    if (option.code === currentCode || changeMutation.isPending) return;
    setPendingCode(option.code);
    changeMutation.mutate(option.code);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[95vw] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Gerenciar plano</DialogTitle>
          <DialogDescription>
            Escolha o plano ideal para o tamanho da sua equipe. A mudança vale a partir do próximo
            ciclo.
          </DialogDescription>
        </DialogHeader>

        {isLoading || !catalog ? (
          <div className="flex items-center justify-center gap-2 py-12 text-ink-soft">
            <IconLoader2 className="animate-spin" size={20} /> Carregando planos…
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-3">
            {catalog.map((plan) => {
              const isCurrent = plan.code === currentCode;
              const loading = pendingCode === plan.code;
              return (
                <div
                  key={plan.code}
                  className={cn(
                    "flex flex-col rounded-2xl border p-4 shadow-soft",
                    isCurrent ? "border-brand-500 ring-1 ring-brand-500/30" : "border-border",
                  )}
                >
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-sm font-bold text-ink">{plan.name}</span>
                    {isCurrent ? (
                      <Badge variant="success">Atual</Badge>
                    ) : plan.highlight ? (
                      <Badge variant="brand">Popular</Badge>
                    ) : null}
                  </div>

                  <p className="mb-3">
                    <span className="text-xl font-bold text-ink">{formatPrice(plan.priceCents)}</span>
                    <span className="text-[12px] text-ink-soft">
                      {plan.billingCycle === "yearly" ? " /ano" : " /mês"}
                    </span>
                  </p>

                  <ul className="mb-4 flex-1 space-y-1.5">
                    {plan.features.map((f) => (
                      <li
                        key={f.label}
                        className={cn(
                          "flex items-start gap-1.5 text-[12px]",
                          f.included ? "text-ink-muted" : "text-ink-soft/60 line-through",
                        )}
                      >
                        <IconCheck
                          size={13}
                          className={cn(
                            "mt-0.5 shrink-0",
                            f.included ? "text-success" : "text-ink-soft/40",
                          )}
                        />
                        {f.label}
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    disabled={isCurrent || changeMutation.isPending}
                    onClick={() => selectPlan(plan)}
                    className={cn(
                      "inline-flex h-10 items-center justify-center gap-2 rounded-xl text-sm font-semibold transition-all",
                      isCurrent
                        ? "cursor-default border border-border text-ink-soft"
                        : "bg-brand-grad text-white shadow-glow hover:scale-[1.01] disabled:opacity-60",
                    )}
                  >
                    {loading ? (
                      <IconLoader2 className="animate-spin" size={16} />
                    ) : isCurrent ? (
                      "Plano atual"
                    ) : (
                      "Mudar para este"
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
